import { globalStyle, style, styleVariants } from "@vanilla-extract/css";
import {
  primitiveTokens,
  semanticThemeTokens
} from "../../theme/ThemeTokens.css";
import { selectGroup } from "../selectGroup/SelectGroup.css";

const { select, transition, divider } = semanticThemeTokens;

/*
 * Shared Constants
 */
const sharedPadding = {
  padding: primitiveTokens.sizing["2x"]
};

/*
 * Trigger & Value
 */
export const selectTrigger = style({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "nowrap",
  alignSelf: "stretch",
  width: "100%",
  borderRadius: primitiveTokens.radii.sm,
  borderWidth: primitiveTokens.strokes.md,
  borderStyle: "solid",
  borderColor: select.default.borderColor,
  backgroundColor: select.default.backgroundColor,
  color: select.default.textColor,
  padding: primitiveTokens.sizing["2x"],
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  cursor: "pointer",
  transition: transition,
  selectors: {
    // Style the select slightly different if its inside the select group
    [`${selectGroup} &`]: {
      borderRadius: 0
    },
    [`${selectGroup} > &:first-child, ${selectGroup} > :first-child &`]: {
      borderTopLeftRadius: primitiveTokens.radii.sm,
      borderBottomLeftRadius: primitiveTokens.radii.sm
    },
    [`${selectGroup} > &:last-child, ${selectGroup} > :last-child &`]: {
      borderTopRightRadius: primitiveTokens.radii.sm,
      borderBottomRightRadius: primitiveTokens.radii.sm
    },
    "&[data-disabled]": {
      cursor: "default",
      backgroundColor: select.disabled.backgroundColor,
      borderColor: select.disabled.borderColor,
      color: select.disabled.textColor
    },
    '&:not([data-placeholder="true"])': {
      color: select.default.textColorSelected
    },
    "&:not([data-disabled]):hover": {
      backgroundColor: select.hover.backgroundColor
    },
    "&:focus-visible": {
      outline: "none",
      backgroundColor: select.focus.backgroundColor,
      boxShadow: primitiveTokens.colors.shadow.focusRing
    },
    '&[data-state="open"]': {
      backgroundColor: select.focus.backgroundColor
    },
    '&[data-disabled="true"]': {
      backgroundColor: select.disabled.backgroundColor,
      borderColor: select.disabled.borderColor
    },
    "&.inputError": {
      borderColor: semanticThemeTokens.input.invalid.borderColor,
      backgroundColor: semanticThemeTokens.input.invalid.backgroundColor
    },
    "&.inputError:not([data-disabled]):hover": {
      borderColor: semanticThemeTokens.input.invalid.borderColor
    },
    "&.inputError:focus-visible": {
      borderColor: semanticThemeTokens.input.invalid.borderColor,
      outline: "none",
      boxShadow: "none"
    }
  }
});

export const selectTriggerSizes = styleVariants({
  md: {
    height: primitiveTokens.sizing["10x"],
    fontSize: primitiveTokens.type.sm
  },
  sm: {
    height: primitiveTokens.sizing["8x"],
    fontSize: primitiveTokens.type.xs
  }
});

export const selectTriggerText = style({
  padding: primitiveTokens.sizing["2x"]
});

/*
 * Popover & Dialog
 */
export const selectPopover = style({
  minWidth: "var(--radix-popover-trigger-width)",
  borderRadius: primitiveTokens.radii.sm,
  boxShadow: primitiveTokens.colors.shadow.popover,
  backdropFilter: "blur(calc(var(--blur, 24px) / 2))",
  display: "flex",
  flexDirection: "column",
  color: select.default.textColor,
  backgroundColor: select.default.backgroundColor
});

/*
 * Search Input
 */
export const selectSearchWrapper = style({
  display: "flex",
  alignItems: "center",
  alignSelf: "stretch",
  borderBottomWidth: primitiveTokens.borderWidth.default,
  borderBottomColor: divider.backgroundColor,
  borderBottomStyle: "solid",
  height: primitiveTokens.sizing["10x"],
  padding: `${primitiveTokens.sizing["0x"]} ${primitiveTokens.sizing["4x"]}`,
  gap: "10px"
});

export const selectSearchInput = style({
  border: "none",
  outline: "none",
  fontSize: primitiveTokens.type.sm,
  backgroundColor: "transparent",
  color: select.menuList.listItem.textColor
});

/*
 * List & Items
 */
export const selectList = style({
  overflowY: "auto",
  maxHeight: "calc(300px - 2.5rem)",
  ...sharedPadding,
  marginTop: 0,
  selectors: {
    "&::-webkit-scrollbar": {
      width: primitiveTokens.sizing["2x"]
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: primitiveTokens.colors.neutral["200"],
      borderRadius: primitiveTokens.radii.sm
    },
    "&:focus": {
      outline: "none"
    }
  }
});

export const selectItem = style({
  color: select.menuList.listItem.textColor,
  position: "relative",
  gap: primitiveTokens.sizing["2x"],
  display: "flex",
  ...sharedPadding,
  cursor: "pointer",
  transition: transition,
  userSelect: "none",
  alignItems: "center",
  borderRadius: primitiveTokens.radii.xs,
  selectors: {
    // Highlight is owned by hover + the keyboard "virtual focus" (data-active
    // set by useSelectKeyboard). We intentionally do NOT style cmdk's own
    // `aria-selected` — cmdk pins it to its first item, which can't be moved
    // once the popover owns navigation, and would read as a stuck highlight.
    "&:hover": {
      backgroundColor: select.menuList.listItem.hover.backgroundColor,
      borderRadius: primitiveTokens.radii.xs
    },
    '&[data-active="true"]': {
      backgroundColor: select.menuList.listItem.hover.backgroundColor,
      borderRadius: primitiveTokens.radii.xs
    },
    "&:disabled": {
      opacity: 0.5
    },
    "&:disabled:hover": {
      backgroundColor: "unset"
    },
    "&:focus": {
      outline: "none"
    }
  }
});

export const selectItemSizes = styleVariants({
  md: {
    height: 45,
    fontSize: primitiveTokens.type.sm
  },
  sm: {
    height: primitiveTokens.sizing["7x"],
    fontSize: primitiveTokens.type.xs
  }
});

/**
 * The creatable row (`allowCreate`). Italic and set off by a rule from the
 * options above it, so "add a new value" doesn't read as one of them.
 */
export const selectItemCreate = style({
  fontStyle: "italic",
  selectors: {
    "&:not(:first-child)": {
      borderTop: `1px solid ${divider.backgroundColor}`
    }
  }
});

export const selectItemSelected = style({
  backgroundColor: select.menuList.listItem.selected.backgroundColor,
  borderRadius: primitiveTokens.radii.xs
});

/**
 * Item layout for entries that carry a secondary description line.
 * Lets the row grow vertically (instead of being clamped by
 * `selectItemSizes`) so a label + description stack fits comfortably.
 */
export const selectItemWithDescription = style({
  height: "auto",
  paddingTop: primitiveTokens.sizing["1x"],
  paddingBottom: primitiveTokens.sizing["1x"],
  fontSize: primitiveTokens.type.xs,
  alignItems: "flex-start"
});

export const selectItemTextStack = style({
  display: "flex",
  flexDirection: "column",
  gap: 0,
  minWidth: 0,
  width: "100%"
});

export const selectItemLabel = style({
  fontSize: primitiveTokens.type.xs,
  lineHeight: 1.3
});

/**
 * Secondary text under the option label. Dimmed via `opacity` so it
 * reads as supporting context without introducing a new theme token.
 */
export const selectItemDescription = style({
  fontSize: primitiveTokens.type.xs,
  lineHeight: 1.3,
  opacity: 0.65
});

export const selectListSecondaryState = style({
  ...sharedPadding,
  textAlign: "center"
});

export const selectListSecondaryStateSizes = styleVariants({
  md: {
    fontSize: primitiveTokens.type.sm
  },
  sm: {
    fontSize: primitiveTokens.type.xs
  }
});

/*
 * MultiSelect & Utility
 */
export const selectRowBetween = style({
  display: "flex",
  // Fills the trigger minus its chrome, so the chip fit measures the width
  // the chips actually have.
  flex: "1 1 auto",
  justifyContent: "space-between",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  alignItems: "center",
  selectors: {
    "[data-disabled] &": {
      color: select.disabled.textColor
    }
  }
});

export const selectWrapCenter = style({
  display: "flex",
  alignItems: "center",
  width: "100%",
  gap: primitiveTokens.sizing["2x"]
});

export const selectFooterWrapper = style({
  ...sharedPadding,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: primitiveTokens.sizing["13x"]
});

export const selectFooterAction = style({
  ...sharedPadding,
  display: "flex",
  justifyContent: "center",
  borderTop: `1px solid ${divider.backgroundColor}`
});

globalStyle(`${selectFooterAction} > *`, {
  flex: 1,
  justifyContent: "center"
});

const sharedFlexContainer = {
  flex: 1,
  justifyContent: "center",
  cursor: "pointer",
  display: "flex"
};

export const selectCenterGrow = style({
  ...sharedFlexContainer,
  marginRight: primitiveTokens.sizing["5x"]
});

export const selectCenterInteractive = style({
  ...sharedFlexContainer,
  maxWidth: "100%",
  padding: primitiveTokens.sizing["2x"]
});

export const triggerActionsContainer = style({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  alignSelf: "stretch",
  borderLeft: `1px solid ${divider.backgroundColor}`,
  borderRight: `1px solid ${divider.backgroundColor}`,
  marginTop: primitiveTokens.sizing["1x"],
  marginBottom: primitiveTokens.sizing["1x"],
  paddingLeft: primitiveTokens.sizing["1x"],
  paddingRight: primitiveTokens.sizing["1x"],
  selectors: {
    "&:empty": {
      display: "none"
    }
  }
});

export const selectButtonLayout = style({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  color: select.icon.color,
  selectors: {
    "[data-disabled] &": {
      color: select.icon.disabledColor
    }
  }
});

export const chevronWrapper = style({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: `0 ${primitiveTokens.sizing["2x"]}`,
  flexShrink: 0
});

// Marginless: each neighbouring icon wrapper owns 8px, so the divider sits an
// equal distance from the × and the chevron.
export const separator = style({
  flexGrow: 1,
  height: primitiveTokens.sizing["4x"]
});

// The × owns the whitespace on both of its sides, so it stays centred between
// the trigger-actions border and the pre-chevron divider.
export const closeButtonWrapper = style({
  display: "flex",
  alignItems: "center",
  gap: 0,
  padding: `0 ${primitiveTokens.sizing["2x"]}`,
  margin: 0
});

export const selectPortalRoot = style({
  zIndex: 1050
});

export const fieldContainer = style({
  display: "flex",
  flexDirection: "column",
  gap: primitiveTokens.sizing["1x"],
  minWidth: "200px",
  height: "auto"
});

export const triggerChip = style({
  flexShrink: 0
});

export const virtualizedItem = style({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%"
});

/**
 * An option's label in `MultiSelect`. Below the relevance divider the text
 * dims with its ghost checkbox, so the whole row reads as the lesser group.
 */
export const optionLabel = style({
  selectors: {
    '&[data-relevant="false"]': {
      opacity: 0.6
    }
  }
});

/**
 * The single divider between relevant and non-relevant options. Absolutely
 * positioned inside the virtualized list; the `top` offset is set inline from
 * the boundary row's measured position.
 */
export const relevanceDivider = style({
  position: "absolute",
  left: 0,
  right: 0,
  zIndex: 1
});
export const selectedAll = style({
  padding: primitiveTokens.sizing["4x"]
});

export const pasteReport = style({
  margin: `${primitiveTokens.sizing["2x"]} ${primitiveTokens.sizing["2x"]} 0`
});
